import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// 3 letter month abbreviations in german:
const months = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

const daysOfWeek = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** Formats a UTC timestamp as local time (HH:mm). */
export const formatTime = (timestamp: string) => {
  return dayjs.utc(timestamp).local().format("HH:mm");
};

/** Formats a UTC date for display in local time (e.g. "Mo 08.5.26"). */
export const formatDateUI = (date: any, continous?: boolean | undefined) => {
  const inputDate = new Date(date);
  const day = String(inputDate.getDate()).padStart(2, "0");
  const year = inputDate.getFullYear() - 2000;
  const month = inputDate.getMonth() + 1;
  const dayOfWeek = daysOfWeek[inputDate.getDay()];

  if (continous) {
    return dayOfWeek;
  } else {
    return `${dayOfWeek} ${day}.${month}.${year}`;
  }
};

export const formatDateInternals = (date: any) => {
  if (typeof date === "string") {
    // Check if the date is in "yyyy-mm-dd" format
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-");
      return `${year}-${month}-${day}`;
    }
    // If it's not in the expected format, you may need additional parsing logic
    // Handle other date formats here
  } else if (date instanceof Date) {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  return "Invalid Date";
};

export const formatDateDisplay = (date: any) => {
  if (typeof date === "string") {
    // Date-only strings stay calendar dates — no timezone shift.
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-");
      return `${day}.${month}.${year}`;
    }
  }
  // Date instances and parseable strings (e.g. the ISO timestamps DTOs
  // carry) format as the LOCAL calendar date.
  if (date != null && (date instanceof Date || typeof date === "string")) {
    const parsed = date instanceof Date ? date : new Date(date);
    if (!Number.isNaN(parsed.getTime())) {
      const day = parsed.getDate().toString().padStart(2, "0");
      const month = (parsed.getMonth() + 1).toString().padStart(2, "0");
      const year = parsed.getFullYear();
      return `${day}.${month}.${year}`;
    }
  }

  return "Invalid Date";
};

/**
 * The German calendar date (DD.MM.YYYY) of a value the API sent, or null when it
 * cannot be read — so a caller shows its own fallback text instead of a raw ISO
 * string or the literal "Invalid Date". A value that is already DD.MM.YYYY is
 * returned as is: `new Date("01.09.2026")` would read it as 9 January.
 */
export const formatDateDisplayOrNull = (date: unknown): string | null => {
  if (typeof date === "string" && /^\d{2}\.\d{2}\.\d{4}$/.test(date)) return date;
  const formatted = formatDateDisplay(date);
  return formatted === "Invalid Date" ? null : formatted;
};

/**
 * Get a human-readable date label with German day abbreviation
 * Examples: "Heute", "Morgen", "Gestern", "Mo 12.03.", "Di 15.04."
 */
export const getDateLabel = (dateStr: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDateObj = new Date(dateStr);
  selectedDateObj.setHours(0, 0, 0, 0);

  const diffTime = selectedDateObj.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Heute";
  if (diffDays === 1) return "Morgen";
  if (diffDays === -1) return "Gestern";

  const dayAbbrev = daysOfWeek[selectedDateObj.getDay()];
  const formattedDate = selectedDateObj.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
  });

  return `${dayAbbrev} ${formattedDate}`;
};

/**
 * Format a date to German short format (DD.MM.)
 */
export const formatDateShort = (dateString: string | Date): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
  });
};

/**
 * Converts a UTC date string to a local Day.js object.
 * @param utcDate - The UTC date string to convert.
 * @returns A Day.js object in the local timezone.
 */
export const toLocalDayjs = (utcDate: string): dayjs.Dayjs => {
  return dayjs.utc(utcDate).local();
};
